// @react-functional

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { trackAddDecisionWatcher } from '../../search-utils';
import { useUser } from '../../../user/useUser';
import { useRestrictedModal } from '../../../components/modal/useRestrictedModal';
import {
  WatcherSearchQuery,
  WatcherSearchQueryEnum,
} from '../../../watchers/watchers.types';
import { StateType } from '../../../state/reducers';
import {
  createWatcherWithTracking,
  deleteWatcherWithTracking,
  createWatcherQueriesWithTracking,
  deleteWatcherQueryWithTracking,
} from '../../../watchers/state/actions';
import { useModal } from '../../../components/modal/useModal';
import { DeleteWatcherModal } from '../../../watchers/delete/DeleteWatcherModal';

type Props = {
  source: string;
  searchType?: WatcherSearchQueryEnum;
};

export const useWatcherButton = ({
  source,
  searchType = WatcherSearchQueryEnum.DECISION,
}: Props): [
  (searchQueries: Array<WatcherSearchQuery>) => void,
  boolean,
  () => void,
] => {
  const {
    isWatched,
    watcherKey,
    alreadyHasAQuery,
    hasMoreThanOneQuery,
  } = useSelector(({ watcherExists }: StateType) => {
    return {
      isWatched:
        !!watcherExists.watcherKey && watcherExists.types.includes(searchType),
      watcherKey: watcherExists.watcherKey,
      alreadyHasAQuery: watcherExists.types.length > 0,
      hasMoreThanOneQuery: watcherExists.types.length > 1,
    };
  });

  const dispatch = useDispatch();

  const { accessPremium } = useUser();
  const { openRestrictedModal } = useRestrictedModal();

  const onDeleteWatcher = () => {
    try {
      if (watcherKey) {
        if (hasMoreThanOneQuery) {
          dispatch(
            deleteWatcherQueryWithTracking(watcherKey, searchType, source),
          );
        } else {
          dispatch(deleteWatcherWithTracking(watcherKey, source));
        }
      }
    } catch (error) {
      throw error;
    }
  };

  const { openModal: openDeleteWatcherModal } = useModal(
    modalProps => (
      <DeleteWatcherModal deleteWatcher={onDeleteWatcher} {...modalProps} />
    ),
    [onDeleteWatcher],
  );

  const onCreateWatcher = async (searchQueries: Array<WatcherSearchQuery>) => {
    try {
      if (alreadyHasAQuery && watcherKey) {
        dispatch(
          createWatcherQueriesWithTracking(watcherKey, searchQueries, source),
        );
      } else {
        dispatch(createWatcherWithTracking(searchQueries, source));
      }
    } catch (error) {
      throw error;
    }
  };

  const createWatcher = (searchQueries: Array<WatcherSearchQuery>) => {
    if (!accessPremium) {
      openRestrictedModal({ source });
    } else {
      trackAddDecisionWatcher(source);
      onCreateWatcher(searchQueries);
    }
  };

  const deleteWatcher = () => {
    if (!accessPremium) {
      openRestrictedModal({ source });
    } else {
      openDeleteWatcherModal();
    }
  };

  return [createWatcher, isWatched, deleteWatcher];
};
